import { inject as historyInject } from '@angular/core';
import { selectCarState as selectHistoryState } from '../../store/selectors/car.selectors';
import { MapListingCards } from '../../services/map-listing-cards.service';
import { untracked as mapUntracked } from '@angular/core';
import { createMapViewScroller } from '../../shared/map-view-scroll';
import {
  afterNextRender,
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Injector,
  Input,
  input,
  computed as mapComputed,
  effect as mapEffect,
  signal as mapSignal,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { Actions } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { map, Observable, Subject, takeUntil } from 'rxjs';
import { CarSearchResult } from '../../models/car-model';
import { MapParameters } from '../../models/map-parameters.model';
import { PolygonFilter } from '../../models/polygon-filter.model';
import { MapResultComponent } from '../../shared/components/map-result/map-result.component';
import { Statuses } from '../../shared/constants/status.constants';
import * as paginationActions from '../../store/actions/car.actions';
import {
  changeCarPage,
  changeCarSort,
  clearCarPolygon,
  retryCarSearch,
  setCarMapView,
  setCarPolygon,
} from '../../store/actions/car.actions';
import {
  selectCarMapError,
  selectCarMapListItems,
  selectCarMapLoading,
  selectCarMapSearchResults,
  selectCarPagination,
  selectCarPolygon,
  selectCarPolygonRevision,
  selectCarSearchEmpty,
  selectCarSearchError,
  selectCarSearchLoading,
  selectCarSearchResults,
  selectCarSort,
  selectIsCarMapView,
} from '../../store/selectors/car.selectors';
import { selectIsArabic } from '../../store/selectors/language.selectors';

type SortableCarProperty = 'createdAt' | 'price' | 'kilometers' | 'performance' | 'year';

@Component({
  providers: [MapListingCards],
  selector: 'app-car-search-result',
  standalone: false,
  templateUrl: './car-search-result.component.html',
  styleUrl: './car-search-result.component.scss',
})
export class CarSearchResultComponent implements AfterViewInit, OnDestroy {
  readonly searchHistoryState = historyInject(Store).selectSignal(selectHistoryState);

  listId = input<string>('carList');

  @Output() mapChanged = new EventEmitter<MapParameters>();
  @Output() onListView = new EventEmitter<void>();

  @ViewChild('listTop') listTop!: ElementRef;
  @ViewChild('mapToggle') private mapToggle?: ElementRef<HTMLElement>;
  private readonly mapViewScroll = createMapViewScroller(
    () => this.mapToggle?.nativeElement,
    () => this.mapMode()
  );

  @ViewChild(MapResultComponent) mapResultComponent!: MapResultComponent;

  @Input() mapParameters?: MapParameters;
  sortProperty = '';
  sortDirection: 'asc' | 'desc' | null = 'asc';
  sortLabel = '';
  sortIcon = '';

  private readonly actions = inject(Actions);
  private readonly injector = inject(Injector);
  private pendingPage: number | null = null;
  private scrollVersion = 0;
  private router = inject(Router);
  private store = inject(Store);
  readonly mapListItems = this.store.selectSignal(selectCarMapListItems);
  readonly mapCards = inject(MapListingCards);
  private readonly mapSnapshot = this.store.selectSignal(selectCarMapSearchResults);
  readonly mapCardLimit = mapSignal(12);
  readonly visibleMapCards = mapComputed(() => this.mapCards.cars(this.mapListItems().slice(0, this.mapCardLimit()).map(item => item.id)));
  private readonly syncMapCards = mapEffect(() => {
    const items = this.mapSnapshot()?.items;
    const ids = this.mapListItems().slice(0, this.mapCardLimit()).map(item => item.id);
    const active = this.mapMode();
    mapUntracked(() => this.mapCards.bind('car', items, ids, active));
  });
  private readonly mapCardOrder = mapComputed(() =>
    this.mapListItems()
      .map((item) => item.id)
      .join(',')
  );
  private readonly resetMapBatch = mapEffect(() => {
    this.mapCardOrder();
    this.mapCardLimit.set(12);
  });
  loadMoreMapCards(): void {
    this.mapCardLimit.update((count) => count + 6);
  }

  readonly mapLoading$ = this.store.select(selectCarMapLoading);
  readonly mapError$ = this.store.select(selectCarMapError);
  private readonly mapMode = this.store.selectSignal(selectIsCarMapView);
  private mapTabListener = () => this.onMapTabActivated();
  private mapTabElement?: HTMLElement;
  private timers = new Set<ReturnType<typeof setTimeout>>();
  polygon$ = this.store.select(selectCarPolygon);
  polygonRevision$ = this.store.select(selectCarPolygonRevision);
  error$ = this.store.select(selectCarSearchError);
  loading$ = this.store.select(selectCarSearchLoading);
  searchEmpty$ = this.store.select(selectCarSearchEmpty);
  applyPolygon(polygon: PolygonFilter | null): void {
    this.mapParameters = undefined;
    this.onListView.emit();
    this.store.dispatch(polygon ? setCarPolygon({ polygon }) : clearCarPolygon());
  }
  retrySearch(): void {
    this.store.dispatch(retryCarSearch());
  }

  isArabic$: Observable<boolean> = this.store.select(selectIsArabic);
  carResults$ = this.store.select(selectCarSearchResults);
  carMapResults$ = this.store.select(selectCarMapSearchResults);
  pagination$ = this.store.select(selectCarPagination);
  carState$ = this.store.select(selectCarSort);
  isMapView$ = this.store.select(selectIsCarMapView);

  visiblePages$: Observable<(number | string)[]> = this.carResults$.pipe(
    map((result) => {
      if (!result) {
        return [];
      }

      const total = result.pages.length;
      const current = result.pageIndex + 1;

      if (total <= 8) {
        return result.pages;
      }

      // Beginning
      if (current <= 3) {
        return [1, 2, 3, 4, '...', total];
      }

      // End
      if (current >= total - 2) {
        return [1, '...', total - 3, total - 2, total - 1, total];
      }

      // Middle
      return [1, '...', current - 1, current, current + 1, '...', total];
    })
  );

  statuses = Statuses;

  private destroy$ = new Subject<void>();

  constructor() {
    this.actions.pipe(takeUntil(this.destroy$)).subscribe((action) => {
      if (action.type === paginationActions.searchCarsSuccess.type) {
        const response = (action as ReturnType<typeof paginationActions.searchCarsSuccess>)
          .carResults;
        if (
          this.pendingPage !== null &&
          response?.pageIndex === this.pendingPage &&
          !this.mapMode()
        ) {
          this.pendingPage = null;
          const version = this.scrollVersion;
          afterNextRender(
            () => {
              if (version !== this.scrollVersion || this.mapMode()) return;
              const anchor = this.listTop?.nativeElement as HTMLElement | undefined;
              if (!anchor) return;
              const header = document.querySelector('header.fixed-top');
              anchor.style.scrollMarginTop =
                (header?.getBoundingClientRect().height ?? 0) + 16 + 'px';
              anchor.scrollIntoView({ behavior: 'smooth', block: 'start' });
            },
            { injector: this.injector }
          );
        }
      } else if (
        (
          [
            paginationActions.searchCars.type,
            paginationActions.searchMapCars.type,
            paginationActions.searchCarsFailure.type,
            paginationActions.setCarMapView.type,
            paginationActions.setCarPolygon.type,
            paginationActions.clearCarPolygon.type,
            paginationActions.resetCarSearch.type,
            paginationActions.retryCarSearch.type,
            paginationActions.ensureCarList.type,
            paginationActions.changeCarPageSize.type,
            '[Auth] Logout',
            '[Auth] Clear Session State',
          ] as string[]
        ).includes(action.type)
      ) {
        this.pendingPage = null;
        this.scrollVersion++;
      }
    });
    this.carState$.pipe(takeUntil(this.destroy$)).subscribe((carSort) => {
      this.sortDirection = carSort.direction;
      this.sortProperty = carSort.active as string;
      this.setSortIcon();
      switch (carSort.active) {
        case 'createdAt':
          this.sortLabel = 'ORDER_BY_DATE';
          break;
        case 'price':
          this.sortLabel = 'ORDER_BY_PRICE';
          break;
        case 'kilometers':
          this.sortLabel = 'ORDER_BY_KILOMETERS';
          break;
        case 'performance':
          this.sortLabel = 'ORDER_BY_PERFORMANCE';
          break;
        case 'year':
          this.sortLabel = 'ORDER_BY_YEAR';
          break;
        default:
          this.sortLabel = 'ORDER_BY';
      }
    });
  }

  ngAfterViewInit(): void {
    const mapTabEl = document.getElementById('map-tab');

    if (mapTabEl) {
      this.mapTabElement = mapTabEl;
      mapTabEl.addEventListener('shown.bs.tab', this.mapTabListener);
    }
  }

  setMapView(isMap: boolean): void {
    const enteringMap = isMap && !this.mapMode();
    this.mapViewScroll.cancel();
    this.store.dispatch(setCarMapView({ isCarMapView: isMap }));

    if (isMap) this.onMapTabActivated();
    if (enteringMap) this.mapViewScroll.request();
  }

  onMapTabActivated(): void {
    this.mapResultComponent?.invalidateMap();
  }

  updateMap(event: MapParameters) {
    this.mapChanged.emit(event);
  }

  setSortIcon(): void {
    if (!this.sortProperty) {
      this.sortIcon = 'bi-filter-square-fill';
    }

    this.sortIcon = this.sortDirection === 'asc' ? 'bi-sort-up' : 'bi-sort-down';
  }

  goToDetails(carId: number): void {
    this.router.navigate(['/cars', carId]);
  }

  setSort(property: SortableCarProperty): void {
    const direction = this.getDefaultSortingDirection(property);
    this.store.dispatch(changeCarSort({ active: property, direction: direction }));
  }

  getDefaultSortingDirection(property: SortableCarProperty): 'asc' | 'desc' {
    if (property === this.sortProperty) {
      const direction = this.sortDirection === 'asc' ? 'desc' : 'asc';

      return direction;
    }
    return 'asc';
  }

  goTo(pageIndex: number): void {
    this.requestPage(pageIndex - 1);
  }

  first(): void {
    this.requestPage(0);
  }

  last(total: number, pageSize: number): void {
    const lastPage = Math.ceil(total / pageSize) - 1;
    this.requestPage(lastPage);
  }

  isLastPage(carResult: CarSearchResult): boolean {
    return carResult.pageIndex >= carResult.pages.length - 1;
  }

  private requestPage(pageIndex: number): void {
    this.pendingPage = this.mapMode() ? null : pageIndex;
    this.scrollVersion++;
    this.store.dispatch(changeCarPage({ pageIndex }));
  }

  ngOnDestroy() {
    this.scrollVersion++;
    this.mapTabElement?.removeEventListener('shown.bs.tab', this.mapTabListener);
    this.timers.forEach(clearTimeout);
    this.timers.clear();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
